import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './InAppNotification.css';

// In-app notification banner. It slides in from the top over everything (modals included), shows the
// notification's title and text with an icon for its kind, can be tapped to open what it is about, and can
// be swiped up or closed. Raise one with: window.dispatchEvent(new CustomEvent('inAppNotification', { detail: { title, body, data } }))
// Tapping fires 'inAppNotificationTap' with the notification's data so the app can navigate.
const SHOW_MS = 6000;

function iconFor(type = '') {
  if (type.includes('booking')) return '📅';
  if (type.includes('question')) return '💬';
  if (type === 'player_update') return '📣';
  if (type === 'lesson_reminder') return '⏰';
  if (type === 'skill_update') return '⭐';
  if (type.includes('photo')) return '📷';
  if (type.includes('slot')) return '🎾';
  return '📝';
}

export default function InAppNotification() {
  const [item, setItem] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const timer = useRef(null);
  const startY = useRef(null);
  const dragY = useRef(0);
  const [drag, setDrag] = useState(0);

  const close = useCallback(() => {
    clearTimeout(timer.current);
    setLeaving(true);
    setTimeout(() => { setItem(null); setLeaving(false); setDrag(0); }, 260);
  }, []);

  useEffect(() => {
    const onShow = (e) => {
      const d = (e && e.detail) || {};
      // A coaching request opens the Accept / Deny pop-up instead of the banner (see Bookings/BookingRequestPopup.js)
      if (d.data && d.data.type === 'booking_request' && d.data.bookingId && !d.data.headcoachCopy) return;
      clearTimeout(timer.current);
      setLeaving(false);
      setDrag(0);
      setItem({ id: Date.now(), title: d.title || 'CoachPad', body: d.body || '', data: d.data || {} });
      timer.current = setTimeout(close, SHOW_MS);
    };
    window.addEventListener('inAppNotification', onShow);
    return () => { window.removeEventListener('inAppNotification', onShow); clearTimeout(timer.current); };
  }, [close]);

  if (!item) return null;
  const type = (item.data && item.data.type) || '';

  const onTouchStart = (e) => { startY.current = e.touches[0].clientY; dragY.current = 0; clearTimeout(timer.current); };
  const onTouchMove = (e) => {
    if (startY.current === null) return;
    const dy = Math.min(0, e.touches[0].clientY - startY.current); // only upwards
    dragY.current = dy; setDrag(dy);
  };
  const onTouchEnd = () => {
    startY.current = null;
    if (dragY.current < -40) close();
    else { setDrag(0); timer.current = setTimeout(close, 3000); }
  };
  const open = () => {
    window.dispatchEvent(new CustomEvent('inAppNotificationTap', { detail: item.data }));
    close();
  };

  return createPortal(
    <div className="cp-notif-wrap" role="alert" aria-live="assertive">
      <div
        key={item.id}
        className={`cp-notif${leaving ? ' cp-notif--leaving' : ''}`}
        style={drag ? { transform: `translateY(${drag}px)`, transition: 'none' } : undefined}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        onClick={open}
      >
        <div className="cp-notif__icon" aria-hidden="true">{iconFor(type)}</div>
        <div className="cp-notif__text">
          <div className="cp-notif__title">{item.title}</div>
          {item.body && <div className="cp-notif__body">{item.body}</div>}
        </div>
        <button type="button" className="cp-notif__close" aria-label="Dismiss" onClick={(e) => { e.stopPropagation(); close(); }}>✕</button>
        <div className="cp-notif__bar" style={{ animationDuration: `${SHOW_MS}ms` }} />
      </div>
    </div>,
    document.body
  );
}
